import React from 'react';
import { useSelector } from 'react-redux';

import { listYourHorseGoesToWizard } from '../../util/onboardingCohort';
import { CF_SELLER_LANDING_URL, HANDOFF_DESTINATION } from '../../util/cfHandoff';
import CheckoutHandoffLink from '../CheckoutHandoffLink/CheckoutHandoffLink';
import NamedLink from '../NamedLink/NamedLink';

// Story 3.16 AC1.2 — the local CF_SELLER_LANDING_URL constant that used to be
// declared here now lives in util/cfHandoff.js, the SOLE reader of the CF env
// vars. It is re-exported because the existing test suite and call sites
// import it from this module; the value and its meaning are unchanged.
export { CF_SELLER_LANDING_URL };

/**
 * The ONE "List your horse" entry point. Renders EITHER an internal NamedLink to
 * the in-app listing wizard (NewListingPage) for any logged-in user with a
 * positive selling signal — cfPurchaseId, idle listing credits, any owned
 * listing/draft — or with still-unresolved data (safe default), OR an external
 * anchor to the CF seller landing page for logged-out visitors (straight to CF,
 * no forced login / auth bounce) and query-confirmed brand-new users.
 *
 * Routing is decided by the shared `listYourHorseGoesToWizard` predicate reading
 * currentUser, hasAnyOwnListing, and isAuthenticated from the store, so every
 * one of the nine call sites shares identical logic with no per-site wiring
 * (this is what prevented the mobile-link drift). Extra props (className,
 * aria-label, …) pass through to whichever element renders — except that
 * NamedLink keeps a fixed prop whitelist and drops onClick (ticket 73).
 *
 * ---------------------------------------------------------------------------
 * Story 3.16 (AC1a.1, decision F) — the external branch is now a
 * <CheckoutHandoffLink>, which renders THE SAME `<a href>` with the same
 * attributes and adds a click-time gate. The predicate above is UNCHANGED in
 * behaviour (AC1.7): it still decides internal-vs-external at render time on
 * the mirror. What changed is that taking the external branch no longer means
 * going straight to ClickFunnels — the authority gets asked first, once, on the
 * click. See util/cfHandoff.js for why that read moved from render to click.
 * ---------------------------------------------------------------------------
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.children link contents (label + any icon markup)
 * @param {string} [props.className]
 * @param {Function} [props.onHandled] forwarded to the gated anchor; see
 *   CheckoutHandoffLink. Not wired on the wizard branch: NamedLink forwards no
 *   onClick, so the page change is what ends the click there (ticket 73).
 * @returns {JSX.Element}
 */
const ListYourHorseLink = ({ children, className, onHandled, ...rest }) => {
  const currentUser = useSelector(state => state.user.currentUser);
  const hasAnyOwnListing = useSelector(state => state.user.hasAnyOwnListing);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

  if (listYourHorseGoesToWizard(currentUser, { hasAnyOwnListing, isAuthenticated })) {
    return (
      <NamedLink name="NewListingPage" className={className} {...rest}>
        {children}
      </NamedLink>
    );
  }

  return (
    <CheckoutHandoffLink
      className={className}
      destination={HANDOFF_DESTINATION.SELLER_LANDING}
      onHandled={onHandled}
      {...rest}
    >
      {children}
    </CheckoutHandoffLink>
  );
};

export default ListYourHorseLink;
